import { useMemo, useState } from "react";
import { Stack } from "@mantine/core";
import { DatePicker, InputDropdown } from "@onengine/component-library";
import type { DropdownItem } from "@onengine/component-library";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import { Button } from "@/components/ui/Button/Button";
import { useRoleOptions } from "@/hooks/user-management/useUserManagementQueries";
import type { FormValues } from "@/routes/user-management/add-user";
import {
  ADD_TEMPORARY_ROLE,
  END_DATE,
  removeTemporaryRoleLabel,
  TEMPORARY_ROLE,
} from "./newUserCopy";
import scopeStyles from "@/components/features/RolesPermissions/SetScopeModal/SetScopeModal.module.scss";
import styles from "./add-user.module.scss";

const todayAsDate = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
};

interface TemporaryRoleRowProps {
  index: number;
  onRemove: () => void;
}

/**
 * Each row searches on its own: with one shared search, typing in a row replaced the options
 * every other role picker drew its chips from, and their picks vanished from view.
 */
function TemporaryRoleRow({ index, onRemove }: TemporaryRoleRowProps) {
  const {
    control,
    formState: { errors },
  } = useFormContext<FormValues>();
  const {
    data: options,
    isLoading,
    onSearchChange,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useRoleOptions();
  const [picked, setPicked] = useState<DropdownItem | null>(null);
  const rowErrors = errors.newTemporaryRoles?.[index];

  const items = useMemo<DropdownItem[]>(() => {
    const listed = (options ?? []) as DropdownItem[];
    return picked && !listed.some((option) => option.value === picked.value)
      ? [picked, ...listed]
      : listed;
  }, [options, picked]);

  return (
    <div className={scopeStyles.ruleRow}>
      <Controller
        name={`newTemporaryRoles.${index}.roleId`}
        control={control}
        render={({ field }) => (
          <InputDropdown
            label={TEMPORARY_ROLE}
            placeholder="Select.."
            searchable
            items={items}
            loading={isLoading || isFetchingNextPage}
            onSearch={onSearchChange}
            hasMore={!!hasNextPage}
            onLoadMore={() => {
              void fetchNextPage();
            }}
            error={rowErrors?.roleId?.message ?? false}
            value={field.value || null}
            onChange={(value) => {
              const roleId = (value as string | null) ?? "";
              setPicked(items.find((option) => option.value === roleId) ?? null);
              field.onChange(roleId);
            }}
          />
        )}
      />
      <Controller
        name={`newTemporaryRoles.${index}.endsOn`}
        control={control}
        render={({ field }) => (
          <DatePicker
            label={END_DATE}
            placeholder="MM / DD / YYYY"
            valueFormat="MM/DD/YYYY"
            minDate={todayAsDate()}
            error={rowErrors?.endsOn?.message ?? false}
            value={field.value ?? null}
            onChange={(value) => field.onChange(value ?? null)}
            className={styles.endDate}
          />
        )}
      />
      <div className={scopeStyles.ruleActions}>
        <Button
          type="text"
          variant="text-destructive-primary"
          size="base"
          aria-label={removeTemporaryRoleLabel(picked?.label ?? null)}
          onClick={onRemove}
        >
          <IconTrash size={16} />
        </Button>
      </div>
    </div>
  );
}

/**
 * `new-user-details`: one role and its end date per row, on the scope rules' own row grid.
 * One role per row because the access route takes one role per call, and a calendar date because
 * a temporary role ends at the end of its last day in New York, not at a chosen time.
 */
export function TemporaryRoleRows() {
  const { control } = useFormContext<FormValues>();
  const { fields, append, remove } = useFieldArray({ control, name: "newTemporaryRoles" });

  return (
    <Stack gap="var(--spacing-medium)">
      {fields.map((field, index) => (
        <TemporaryRoleRow key={field.id} index={index} onRemove={() => remove(index)} />
      ))}
      <div className={scopeStyles.addRuleButtons}>
        <Button
          type="text"
          variant="text-secondary"
          size="small"
          iconLeft={<IconPlus size={16} />}
          onClick={() => append({ roleId: "", endsOn: null })}
        >
          {ADD_TEMPORARY_ROLE}
        </Button>
      </div>
    </Stack>
  );
}
